/**
 * EditCompanyDrawer — drawer scivolante per modifica dettagli azienda.
 *
 * Fetch async di AdminCustomerDetail al mount, form prefilled, save via
 * adminApi.customers.update con reason obbligatorio.
 *
 * Wired da AdminCustomersPage (quick action "Modifica dettagli").
 */
import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { adminApi, type AdminCustomerDetail } from '@/services/adminApi';

export interface EditCompanyDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string | null;
  onSaved?: () => void;
}

export function EditCompanyDrawer({ open, onOpenChange, companyId, onSaved }: EditCompanyDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<AdminCustomerDetail | null>(null);
  const [form, setForm] = useState({
    name: '',
    publicName: '',
    description: '',
    employeeCount: '',
    reason: '',
  });

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await adminApi.customers.detail(companyId);
        if (cancelled) return;
        setDetail(d);
        setForm({
          name: d.name ?? '',
          publicName: d.publicName ?? '',
          description: d.description ?? '',
          employeeCount: d.employeeCount != null ? String(d.employeeCount) : '',
          reason: '',
        });
      } catch (err) {
        if (!cancelled) toast.error((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const handleSave = async () => {
    if (!companyId || !detail || !form.reason.trim() || busy) return;
    setBusy(true);
    try {
      const patch: Parameters<typeof adminApi.customers.update>[1] = {
        reason: form.reason.trim(),
      };
      // Send only changed fields to minimize audit noise.
      if (form.name !== detail.name) patch.name = form.name;
      if (form.publicName !== detail.publicName) patch.publicName = form.publicName;
      if (form.description !== (detail.description ?? '')) patch.description = form.description;
      const empCountNum = form.employeeCount.trim() === '' ? undefined : Number(form.employeeCount);
      if (empCountNum !== detail.employeeCount && empCountNum != null && Number.isFinite(empCountNum)) {
        patch.employeeCount = empCountNum;
      }
      await adminApi.customers.update(companyId, patch);
      toast.success('Company updated');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Modifica azienda</SheetTitle>
          <SheetDescription>
            {detail ? (
              <>
                ID: <code className="text-xs">{detail.id}</code>
                {detail.slug && <> · slug: <code className="text-xs">{detail.slug}</code></>}
              </>
            ) : 'Caricamento dettagli...'}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-12 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && detail && (
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Nome legale</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="publicName">Nome pubblico</Label>
              <Input
                id="publicName"
                value={form.publicName}
                onChange={(e) => setForm((f) => ({ ...f, publicName: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="employeeCount">Numero dipendenti</Label>
              <Input
                id="employeeCount"
                type="number"
                min={0}
                value={form.employeeCount}
                onChange={(e) => setForm((f) => ({ ...f, employeeCount: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5 pt-2 border-t border-border/50">
              <Label htmlFor="reason">Reason (richiesto per audit log)</Label>
              <Input
                id="reason"
                placeholder="Brief reason for change..."
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                disabled={busy}
              />
            </div>
          </div>
        )}

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={busy || !form.reason.trim() || !detail}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Salva
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
